import { z } from "zod";
import { bookProductionProductSchema } from "./book-production-evidence.js";
export const BOOK_PRINT_COMPILATION_PLAN_CONTRACT = "BOOK_PRINT_COMPILATION_PLAN_V1";
export const BOOK_VENDOR_PACKAGE_PREFLIGHT_CONTRACT = "BOOK_VENDOR_PACKAGE_PREFLIGHT_V1";
export const BOOK_APPROVED_PACKAGE_CONTRACT = "BOOK_APPROVED_PACKAGE_V1";
export const BOOK_PRINT_APPROVAL_CONTRACT = "BOOK_PRINT_APPROVAL_V1";
export const BOOK_EXTERNAL_PDF_VALIDATION_EVIDENCE_CONTRACT = "BOOK_EXTERNAL_PDF_VALIDATION_EVIDENCE_V1";
const safeIdSchema = z
    .string()
    .trim()
    .min(1)
    .max(240)
    .regex(/^[A-Za-z0-9][A-Za-z0-9_.:/-]*$/u);
const safeCodeSchema = z
    .string()
    .trim()
    .min(1)
    .max(120)
    .regex(/^[A-Z0-9][A-Z0-9_]*$/u);
const sha256Schema = z.string().regex(/^[a-f0-9]{64}$/u);
const timestampSchema = z.string().datetime({ offset: true });
export const createBookPrintPackageCandidateCommandSchema = z
    .object({
    operationId: safeIdSchema,
    previewId: safeIdSchema,
    expectedRevision: z.number().int().nonnegative(),
    expectedProfileId: safeIdSchema,
    expectedProfileVersion: safeIdSchema,
    expectedSnapshotDigestSha256: sha256Schema,
})
    .strict();
export const approveBookPrintPackageCommandSchema = z
    .object({
    previewId: safeIdSchema,
    expectedRevision: z.number().int().nonnegative(),
    expectedProfileId: safeIdSchema,
    expectedProfileVersion: safeIdSchema,
    expectedSnapshotDigestSha256: sha256Schema,
    expectedCoverChecksumSha256: sha256Schema,
    expectedInteriorChecksumSha256: sha256Schema,
    expectedPreflightDigestSha256: sha256Schema,
    expectedCurrentPackageId: safeIdSchema.nullable(),
})
    .strict();
export const invalidateBookPrintPackageCommandSchema = z
    .object({
    expectedRevision: z.number().int().nonnegative(),
    reasonCode: safeCodeSchema,
    expectedCurrentPackageId: safeIdSchema.nullable(),
})
    .strict();
export const recomputeBookPrintPackageEligibilityCommandSchema = z
    .object({
    expectedRevision: z.number().int().nonnegative(),
    expectedCurrentPackageId: safeIdSchema.nullable(),
})
    .strict();
export const bookPrintCompiledPageV1Schema = z
    .object({
    pageId: safeIdSchema,
    productionOrdinal: z.number().int().positive().max(1_000),
    roleCode: safeCodeSchema,
    sectionCode: safeCodeSchema,
    sourceSlotId: safeIdSchema.nullable(),
    semanticNodeIds: z.array(safeIdSchema).max(1_000),
    disposition: z.enum(["POPULATED", "AUTHORIZED_INTENTIONAL_BLANK"]),
    blankAuthorizationEvidenceId: safeIdSchema.nullable(),
})
    .strict()
    .superRefine((page, context) => {
    if ((page.disposition === "POPULATED" &&
        (page.semanticNodeIds.length === 0 || page.blankAuthorizationEvidenceId !== null)) ||
        (page.disposition === "AUTHORIZED_INTENTIONAL_BLANK" &&
            (page.semanticNodeIds.length > 0 || page.blankAuthorizationEvidenceId === null)))
        context.addIssue({
            code: "custom",
            path: ["disposition"],
            message: "Blank and populated pages require mutually exclusive evidence.",
        });
});
export const bookPrintCompilationPlanV1Schema = z
    .object({
    contract: z.literal(BOOK_PRINT_COMPILATION_PLAN_CONTRACT),
    planId: safeIdSchema,
    planDigestSha256: sha256Schema,
    bookId: safeIdSchema,
    revisionId: safeIdSchema,
    product: bookProductionProductSchema,
    editionPolicyId: safeIdSchema,
    profileId: safeIdSchema,
    profileVersion: safeIdSchema,
    semanticSnapshotDigestSha256: sha256Schema,
    compositionRequestDigestSha256: sha256Schema,
    compositionPlanDigestSha256: sha256Schema,
    drawLedgerDigestSha256: sha256Schema,
    cover: z
        .object({
        pageCount: z.literal(1),
        mediaBoxPoints: z.object({ width: z.number().positive(), height: z.number().positive() }),
        coverDesignDigestSha256: sha256Schema,
    })
        .strict(),
    interior: z
        .object({
        pageCount: z.number().int().positive().max(1_000),
        mediaBoxPoints: z.object({ width: z.number().positive(), height: z.number().positive() }),
        pages: z.array(bookPrintCompiledPageV1Schema).min(1).max(1_000),
    })
        .strict(),
    createdAt: timestampSchema,
    truthBoundary: z.literal("COMPILED_PLAN_NOT_RENDERED_PREFLIGHTED_APPROVED_OR_PRINTED"),
})
    .strict()
    .superRefine((plan, context) => {
    if (plan.interior.pageCount !== plan.interior.pages.length ||
        plan.interior.pages.some((page, index) => page.productionOrdinal !== index + 1))
        context.addIssue({
            code: "custom",
            path: ["interior", "pages"],
            message: "Compiled interior pages must be exact, contiguous, and start at one.",
        });
    const nodeIds = plan.interior.pages.flatMap(({ semanticNodeIds }) => semanticNodeIds);
    if (new Set(nodeIds).size !== nodeIds.length)
        context.addIssue({
            code: "custom",
            path: ["interior", "pages"],
            message: "A semantic node cannot be assigned to more than one compiled physical page.",
        });
});
export const bookPrintArtifactReferenceV1Schema = z
    .object({
    kind: z.enum(["CANONICAL_COVER", "CANONICAL_INTERIOR", "ASSEMBLED_VENDOR_PACKAGE"]),
    objectKey: z
        .string()
        .trim()
        .min(1)
        .max(1_024)
        .refine((value) => !value.startsWith("/") && !value.includes(".."), {
        message: "Artifact keys must remain relative immutable object keys.",
    }),
    versionId: z.string().trim().min(1).max(1_024),
    checksumSha256: sha256Schema,
    byteSize: z
        .number()
        .int()
        .positive()
        .max(500 * 1024 * 1024),
    pageCount: z.number().int().positive().max(1_000),
    contentType: z.literal("application/pdf"),
    mediaBoxPoints: z
        .object({ width: z.number().positive(), height: z.number().positive() })
        .nullable(),
})
    .strict()
    .superRefine((artifact, context) => {
    if (artifact.kind === "ASSEMBLED_VENDOR_PACKAGE" && artifact.mediaBoxPoints !== null)
        context.addIssue({
            code: "custom",
            path: ["mediaBoxPoints"],
            message: "An assembled package has mixed cover and interior media boxes.",
        });
    if (artifact.kind !== "ASSEMBLED_VENDOR_PACKAGE" && artifact.mediaBoxPoints === null)
        context.addIssue({
            code: "custom",
            path: ["mediaBoxPoints"],
            message: "Canonical cover and interior artifacts require exact media boxes.",
        });
});
export const bookVendorPreflightFindingV1Schema = z
    .object({
    code: safeCodeSchema,
    severity: z.enum(["INFO", "WARNING", "BLOCKED"]),
    artifactKind: z.enum(["COVER", "INTERIOR", "ASSEMBLED_PACKAGE", "SEMANTIC_PACKAGE"]),
    pageOrdinal: z.number().int().positive().nullable(),
    message: z.string().trim().min(1).max(500),
})
    .strict();
export const bookExternalPdfValidationEvidenceV1Schema = z
    .object({
    contract: z.literal(BOOK_EXTERNAL_PDF_VALIDATION_EVIDENCE_CONTRACT),
    evidenceId: safeIdSchema,
    artifactKind: z.enum(["COVER", "INTERIOR", "ASSEMBLED_PACKAGE"]),
    artifactChecksumSha256: sha256Schema,
    artifactByteSize: z
        .number()
        .int()
        .positive()
        .max(500 * 1024 * 1024),
    validator: z
        .object({
        toolId: safeIdSchema,
        toolVersion: z.string().trim().min(1).max(120),
        executionEnvironmentDigestSha256: sha256Schema,
    })
        .strict(),
    report: z
        .object({
        objectKey: z
            .string()
            .trim()
            .min(1)
            .max(1_024)
            .refine((value) => !value.startsWith("/") && !value.includes(".."), {
            message: "Validator report keys must remain relative immutable object keys.",
        }),
        versionId: z.string().trim().min(1).max(1_024),
        checksumSha256: sha256Schema,
        byteSize: z
            .number()
            .int()
            .positive()
            .max(50 * 1024 * 1024),
        contentType: z.enum(["application/json", "application/xml", "text/plain"]),
    })
        .strict(),
    checks: z
        .object({
        pdfX4: z.boolean(),
        gracol2006OutputIntent: z.boolean(),
        fontsEmbedded: z.boolean(),
        liveSearchableTypeWhereSupported: z.boolean(),
        pageBoxes: z.boolean(),
        effectiveImageDpi: z.boolean(),
        noEncryption: z.boolean(),
        noPrinterMarks: z.boolean(),
    })
        .strict(),
    state: z.enum(["PASS", "FAIL"]),
    validatedAt: timestampSchema,
    truthBoundary: z.literal("EXTERNAL_BYTE_VALIDATION_NOT_HUMAN_VISUAL_REVIEW_OR_PHYSICAL_PRINT_PROOF"),
})
    .strict()
    .superRefine((evidence, context) => {
    if (evidence.state === "PASS" && !Object.values(evidence.checks).every(Boolean))
        context.addIssue({
            code: "custom",
            path: ["state"],
            message: "A passing external report must pass every required PDF check.",
        });
});
export const bookVendorPackagePreflightV1Schema = z
    .object({
    contract: z.literal(BOOK_VENDOR_PACKAGE_PREFLIGHT_CONTRACT),
    preflightId: safeIdSchema,
    preflightDigestSha256: sha256Schema,
    planDigestSha256: sha256Schema,
    profileId: safeIdSchema,
    profileVersion: safeIdSchema,
    stage: z.enum(["CANONICAL_SOURCE", "ASSEMBLED_VENDOR_PACKAGE"]),
    state: z.enum(["PASS", "BLOCKED", "EXTERNAL_VALIDATION_REQUIRED"]),
    checks: z
        .object({
        exactPageCount: z.boolean(),
        exactMediaBoxes: z.boolean(),
        pageBoxes: z.boolean(),
        embeddedFonts: z.boolean(),
        liveSearchableType: z.boolean(),
        effectiveImageDpi: z.boolean(),
        outputIntent: z.boolean(),
        pdfX4: z.boolean(),
        noEncryption: z.boolean(),
        noCropOrRegistrationMarks: z.boolean(),
        safeGutterSpineAndBarcode: z.boolean(),
        semanticRoleCoverage: z.boolean(),
        noUnauthorizedBlanks: z.boolean(),
        noDuplicateOrMissingContent: z.boolean(),
        rightsConsentAndLikeness: z.boolean(),
    })
        .strict(),
    externalPdfValidator: z.enum(["NOT_RUN", "PASS", "FAIL"]),
    externalValidationEvidence: z.array(bookExternalPdfValidationEvidenceV1Schema).max(3),
    findings: z.array(bookVendorPreflightFindingV1Schema).max(2_000),
    evaluatedAt: timestampSchema,
    truthBoundary: z.literal("PREFLIGHT_AGAINST_SUPPLIED_INSPECTION_NOT_PHYSICAL_PRINT_PROOF"),
})
    .strict()
    .superRefine((preflight, context) => {
    const everyCheckPasses = Object.values(preflight.checks).every(Boolean);
    const blockedFinding = preflight.findings.some(({ severity }) => severity === "BLOCKED");
    if (preflight.state === "PASS" &&
        (!everyCheckPasses ||
            blockedFinding ||
            preflight.externalPdfValidator !== "PASS" ||
            preflight.externalValidationEvidence.length === 0 ||
            preflight.externalValidationEvidence.some(({ state }) => state !== "PASS")))
        context.addIssue({
            code: "custom",
            path: ["state"],
            message: "Passing preflight requires every check and external PDF validation to pass.",
        });
    if (preflight.state === "EXTERNAL_VALIDATION_REQUIRED" &&
        (!everyCheckPasses ||
            blockedFinding ||
            preflight.externalPdfValidator !== "NOT_RUN" ||
            preflight.externalValidationEvidence.length !== 0))
        context.addIssue({
            code: "custom",
            path: ["state"],
            message: "Only an otherwise clean preflight may wait for external validation.",
        });
    if (preflight.state === "BLOCKED" &&
        everyCheckPasses &&
        !blockedFinding &&
        preflight.externalPdfValidator !== "FAIL")
        context.addIssue({
            code: "custom",
            path: ["state"],
            message: "A blocked preflight must retain a failed check or blocking finding.",
        });
});
export const bookPublicationExecutionReferenceV1Schema = z
    .object({
    objectKey: z
        .string()
        .trim()
        .min(1)
        .max(1_024)
        .refine((value) => !value.startsWith("/") && !value.includes("..")),
    versionId: z.string().trim().min(1).max(1_024),
    checksumSha256: sha256Schema,
    byteSize: z
        .number()
        .int()
        .positive()
        .max(50 * 1024 * 1024),
    contentType: z.literal("application/json"),
    observedDrawLedgerDigestSha256: sha256Schema,
    publicationPlanDigestSha256: sha256Schema,
    validatedCoverChecksumSha256: sha256Schema,
    validatedInteriorChecksumSha256: sha256Schema,
})
    .strict();
export const bookPrintApprovalV1Schema = z
    .object({
    contract: z.literal(BOOK_PRINT_APPROVAL_CONTRACT),
    approvalId: safeIdSchema,
    packageId: safeIdSchema,
    actorId: safeIdSchema,
    approvedAt: timestampSchema,
    profileId: safeIdSchema,
    profileVersion: safeIdSchema,
    semanticSnapshotDigestSha256: sha256Schema,
    planDigestSha256: sha256Schema,
    coverChecksumSha256: sha256Schema,
    interiorChecksumSha256: sha256Schema,
    preflightDigestSha256: sha256Schema,
    publicationExecutionChecksumSha256: sha256Schema.optional(),
    approvalDigestSha256: sha256Schema,
    truthBoundary: z.literal("EXACT_PRINT_BYTES_APPROVED_NOT_PROVIDER_ACTIVATION_OR_PHYSICAL_PROOF"),
})
    .strict();
export const bookApprovedPackageV1Schema = z
    .object({
    contract: z.literal(BOOK_APPROVED_PACKAGE_CONTRACT),
    packageId: safeIdSchema,
    bookId: safeIdSchema,
    revisionId: safeIdSchema,
    product: bookProductionProductSchema,
    editionPolicyId: safeIdSchema,
    profileId: safeIdSchema,
    profileVersion: safeIdSchema,
    semanticSnapshotDigestSha256: sha256Schema,
    planDigestSha256: sha256Schema,
    compositionPlanDigestSha256: sha256Schema,
    drawLedgerDigestSha256: sha256Schema,
    publicationExecution: bookPublicationExecutionReferenceV1Schema.optional(),
    rightsConsentSnapshotDigestSha256: sha256Schema,
    cover: bookPrintArtifactReferenceV1Schema,
    interior: bookPrintArtifactReferenceV1Schema,
    preflight: bookVendorPackagePreflightV1Schema,
    printApproval: bookPrintApprovalV1Schema.nullable(),
    state: z.enum(["PREFLIGHT_BLOCKED", "READY_FOR_EXACT_PRINT_REVIEW", "PRINT_APPROVED", "STALE"]),
    createdAt: timestampSchema,
    truthBoundary: z.literal("APPROVED_PACKAGE_DOES_NOT_GRANT_PROVIDER_OR_PHYSICAL_SAMPLE_AUTHORITY"),
})
    .strict()
    .superRefine((pkg, context) => {
    if (pkg.publicationExecution &&
        (pkg.publicationExecution.validatedCoverChecksumSha256 !== pkg.cover.checksumSha256 ||
            pkg.publicationExecution.validatedInteriorChecksumSha256 !== pkg.interior.checksumSha256))
        context.addIssue({
            code: "custom",
            path: ["publicationExecution"],
            message: "Executed publication evidence must bind these exact validated PDF bytes.",
        });
    if (pkg.cover.kind !== "CANONICAL_COVER" || pkg.interior.kind !== "CANONICAL_INTERIOR")
        context.addIssue({
            code: "custom",
            path: ["cover"],
            message: "The package must bind canonical cover and interior artifacts.",
        });
    if (pkg.state === "PRINT_APPROVED" && !pkg.printApproval)
        context.addIssue({
            code: "custom",
            path: ["printApproval"],
            message: "A print-approved package requires an exact approval record.",
        });
    if ((pkg.state === "READY_FOR_EXACT_PRINT_REVIEW" || pkg.state === "PRINT_APPROVED") &&
        pkg.preflight.state !== "PASS")
        context.addIssue({
            code: "custom",
            path: ["preflight", "state"],
            message: "Reviewable and approved packages require passing preflight evidence.",
        });
    if (pkg.state !== "PRINT_APPROVED" && pkg.printApproval !== null)
        context.addIssue({
            code: "custom",
            path: ["printApproval"],
            message: "Only the PRINT_APPROVED state may retain exact approval authority.",
        });
    if (pkg.printApproval) {
        const approvalMatches = pkg.printApproval.packageId === pkg.packageId &&
            pkg.printApproval.profileId === pkg.profileId &&
            pkg.printApproval.profileVersion === pkg.profileVersion &&
            pkg.printApproval.semanticSnapshotDigestSha256 === pkg.semanticSnapshotDigestSha256 &&
            pkg.printApproval.planDigestSha256 === pkg.planDigestSha256 &&
            pkg.printApproval.coverChecksumSha256 === pkg.cover.checksumSha256 &&
            pkg.printApproval.interiorChecksumSha256 === pkg.interior.checksumSha256 &&
            pkg.printApproval.preflightDigestSha256 === pkg.preflight.preflightDigestSha256 &&
            pkg.printApproval.publicationExecutionChecksumSha256 ===
                pkg.publicationExecution?.checksumSha256;
        if (!approvalMatches)
            context.addIssue({
                code: "custom",
                path: ["printApproval"],
                message: "Print approval must bind every current exact package dependency.",
            });
    }
});
//# sourceMappingURL=book-approved-package.js.map